/**
 * Owns: privacy-safe event construction, durable queueing, and bounded delivery.
 * Does not own: study behavior, server validation, KPI queries, or user content.
 */

import { validateActivityEvent } from './activity-contract.js?v=1';
import { createAnalyticsOutbox } from './analytics-outbox.js?v=1';

const FIELD_KEYS = new Set([
    'feature',
    'completion_state',
    'duration_bucket',
    'subject_category',
]);

function deviceClass(width) {
    if (!Number.isFinite(width)) return null;
    if (width <= 600) return 'mobile';
    if (width <= 1024) return 'tablet';
    return 'desktop';
}

function eventForTransport(queued) {
    const { attempt_count: _attempts, last_attempt_at: _lastAttempt, ...event } = queued;
    return event;
}

export function createAnalytics({
    outbox = createAnalyticsOutbox(),
    supabase = globalThis.window?.SupabaseClient,
    crypto = globalThis.crypto,
    now = () => new Date(),
    viewportWidth = () => globalThis.window?.innerWidth,
    online = () => globalThis.navigator?.onLine !== false,
    addEventListener = (...args) => globalThis.window?.addEventListener?.(...args),
    appVersion = '2026.08.24.1',
} = {}) {
    let flushPromise = null;
    let initialized = false;

    async function track(eventType, fields = {}) {
        try {
            if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return { queued: false };
            if (Object.keys(fields).some((key) => !FIELD_KEYS.has(key))) return { queued: false };
            if (typeof crypto?.randomUUID !== 'function') return { queued: false };

            const event = {
                event_id: crypto.randomUUID(),
                event_type: eventType,
                occurred_at: now().toISOString(),
                feature: fields.feature,
                completion_state: fields.completion_state,
                duration_bucket: fields.duration_bucket ?? null,
                subject_category: fields.subject_category ?? null,
                device_class: deviceClass(viewportWidth()),
                app_version: appVersion,
                schema_version: 1,
            };
            const validated = validateActivityEvent(event, { now: now() });
            if (!validated.ok) return { queued: false };
            await outbox.enqueue(validated.value);
            return { queued: true };
        } catch {
            return { queued: false };
        }
    }

    async function performFlush() {
        let pending = 0;
        try {
            pending = await outbox.count();
            if (!pending || !online() || !supabase?.auth || !supabase?.functions) {
                return { sent: 0, pending };
            }

            const { data: { session } = {} } = await supabase.auth.getSession();
            if (!session?.user) return { sent: 0, pending };

            const queued = await outbox.peekBatch(50);
            if (!queued.length) return { sent: 0, pending: 0 };
            const ids = queued.map((event) => event.event_id);
            await outbox.markAttempt(ids);

            const { data, error } = await supabase.functions.invoke('ingest-activity', {
                body: { events: queued.map(eventForTransport) },
            });
            if (error || !Array.isArray(data?.accepted)) {
                return { sent: 0, pending: await outbox.count() };
            }

            const sentSet = new Set(ids);
            const accepted = [...new Set(data.accepted.filter((id) => sentSet.has(id)))];
            await outbox.ack(accepted);
            return { sent: accepted.length, pending: await outbox.count() };
        } catch {
            try { pending = await outbox.count(); } catch { pending = 0; }
            return { sent: 0, pending };
        }
    }

    function flush() {
        if (flushPromise) return flushPromise;
        flushPromise = performFlush().finally(() => { flushPromise = null; });
        return flushPromise;
    }

    function onOnline() {
        void flush();
    }

    async function init() {
        if (initialized) return;
        initialized = true;
        addEventListener('online', onOnline);
        try { await outbox.purgeExpired(30); } catch {}
        void flush();
    }

    return Object.freeze({ init, track, flush });
}

const Analytics = typeof window !== 'undefined' ? createAnalytics() : null;
if (typeof window !== 'undefined') window.Analytics = Analytics;

export { Analytics };
export default Analytics;
